import { Box, Card, styled, useTheme } from '@mui/material';
import { H3, H5, Small } from 'components/Typography';
import { Bookmark, Share2 } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import FlexBox from 'components/flexbox/FlexBox';
import FlexRowAlign from 'components/flexbox/FlexRowAlign';

// Default funding data if none provided
const defaultFundingData = [
  {
    id: 1,
    title: '2025년 여성창업 상세세이지 교육 및 컨설팅',
    category: 'AI 한줄 요약',
    amount: '9,500만원',
    deadline: '25.01.06',
    tags: ['서울 전체', '소상공인', '고용안정'],
    description:
      '신산업 책임보험 최대 700만원/년 내 지원이 제공됩니다.\n해외 전시회 참여 1건에 대해 최대 500만원 지원이 제공됩니다.',
    logo: '/static/logo/SMEs.png',
  },
  {
    id: 2,
    title: '2025년 여성창업 상세세이지 교육 및 컨설팅',
    category: 'AI 한줄 요약',
    amount: '9,500만원',
    deadline: '25.01.06',
    tags: ['서울 전체', '소상공인', '고용안정'],
    description:
      '신산업 책임보험 최대 700만원/년 내 지원이 제공됩니다.\n해외 전시회 참여 1건에 대해 최대 500만원 지원이 제공됩니다.',
    logo: '/static/logo/SMEs.png',
  },
];

const StyledCard = styled(Card)(({ theme }) => ({
  width: '100%',
  padding: 0,
  backgroundColor: theme.palette.primary.lightGrey200,
  borderRadius: 0,
  border: 'none',
  [theme.breakpoints.down('sm')]: {
    '& > .MuiBox-root': {
      paddingBottom: 0,
    },
  },
}));

const FundingSectionOnlyScroll = ({
  isMobile,
  fundingData = [],
  title = '정부지원금',
  icon = '/static/icons/money.svg',
  filterItems = ['전체', '금융', '기술', '인력', '수출', '내수', '창업'],
  onCardClick,
  showMoreButton = true,
  moreButtonText = '최신정보 보기',
  ...props
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [selectedFilter, setSelectedFilter] = useState('전체');
  const [bookmarkedItems, setBookmarkedItems] = useState(new Set());
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  const dataToUse = fundingData.length > 0 ? fundingData : defaultFundingData;

  const toggleBookmark = itemId => {
    setBookmarkedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const handleCardClick = (item, e) => {
    // Prevent card click if we were dragging
    if (isDragging) {
      e.preventDefault();
      return;
    }
    if (onCardClick) {
      onCardClick(item);
    } else {
      navigate('/naver/callmetellme/jiwongum/post-detail');
    }
  };

  const handleMouseDown = e => {
    setIsDragging(true);
    setStartX(e.pageX - e.currentTarget.offsetLeft);
    setScrollLeft(e.currentTarget.scrollLeft);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = e => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - e.currentTarget.offsetLeft;
    const walk = (x - startX) * 2; // Scroll speed multiplier
    e.currentTarget.scrollLeft = scrollLeft - walk;
  };

  return (
    <StyledCard
      sx={{
        border: 'none',
        overflow: 'visible',
        ...props.sx,
      }}
    >
      <Box
        sx={{
          width: '100%',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            backgroundColor: '#fff',
            padding: isMobile ? '18px' : '26px',
            borderRadius: '24px',
            boxShadow: '1px 4px 6px rgba(0, 0, 0, 0.05)',
            marginBottom: 2.5,
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            mb={2}
          >
            <FlexRowAlign>
              <img
                src={icon}
                alt="icon"
                style={{ width: '24px', height: '24px', marginRight: '8px' }}
              />
              <H3
                style={{
                  fontSize: '20px',
                }}
              >
                {title}
              </H3>
            </FlexRowAlign>
          </Box>

          {/* 필터 버튼들 */}
          {/* <FilterButtons
            menuItems={filterItems}
            selectedFilter={selectedFilter}
            onFilterChange={setSelectedFilter}
          /> */}

          <Box
            onMouseDown={handleMouseDown}
            onMouseLeave={handleMouseLeave}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            sx={{
              display: 'flex',
              overflowX: 'scroll',
              gap: 3,
              paddingBottom: '8px',
              width: '100%',
              minWidth: 0,
              // Prevent text selection during drag
              userSelect: 'none',
              WebkitUserSelect: 'none',
              MozUserSelect: 'none',
              msUserSelect: 'none',
              // Improve drag experience
              cursor: isDragging ? 'grabbing' : 'grab',
              // Ensure flex items don't shrink
              '& > *': {
                flexShrink: 0,
              },
              '&::-webkit-scrollbar': {
                display: 'none',
              },
              '-ms-overflow-style': 'none',
              'scrollbar-width': 'none',
            }}
          >
            {dataToUse.slice(0, 4).map((item, index) => (
              <Box
                key={item.id}
                onClick={e => handleCardClick(item, e)}
                sx={{
                  width: '360px !important',
                  minWidth: '360px !important',
                  maxWidth: '360px !important',
                  flexShrink: '0 !important',
                  flexGrow: '0 !important',
                  flexBasis: '360px !important',
                  // Prevent text selection during drag
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  MozUserSelect: 'none',
                  msUserSelect: 'none',
                }}
              >
                <Box
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Card Header */}
                  <Box flex={1}>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={isMobile ? 1 : 1.5}
                      mb={isMobile ? 1 : 1.5}
                      flexWrap="wrap"
                    >
                      {item.tags &&
                        item.tags.map((tag, tagIndex) => (
                          <Box
                            key={tagIndex}
                            sx={{
                              backgroundColor: '#E3F0FF',
                              color: theme.palette.primary.mainBlue,
                              padding: isMobile ? '2px 8px' : '4px 12px',
                              borderRadius: '100px',
                              fontSize: isMobile ? '12px' : '13px',
                              fontWeight: '500',
                            }}
                          >
                            {tag}
                          </Box>
                        ))}
                    </Box>
                  </Box>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="flex-start"
                    flexDirection="row"
                    mb={isMobile ? 2 : 3}
                  >
                    <Box
                      sx={{
                        width: isMobile ? '44px' : '50px',
                        height: isMobile ? '44px' : '50px',
                        backgroundColor: '#ffffff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginRight: isMobile ? '12px' : '16px',
                        flexShrink: 0,
                        border: `1px solid ${theme.palette.primary.borderColor}`,
                        borderRadius: '100px',
                      }}
                    >
                      <img
                        src={item.logo || '/static/logo/SMEs.png'}
                        alt="logo"
                        style={{
                          width: isMobile ? '48px' : '52px',
                          borderRadius: '100px',
                        }}
                      />
                    </Box>
                    <H5
                      sx={{
                        fontWeight: 'bold',
                        lineHeight: 1.3,
                        marginBottom: '8px',
                        fontSize: isMobile ? '16px' : '18px',
                      }}
                    >
                      {item.title}
                    </H5>
                  </Box>

                  <Box mb={isMobile ? 2 : 3} sx={{ flexGrow: 1 }}>
                    <Box
                      sx={{
                        padding: '16px',
                        borderRadius: '24px',
                        backgroundColor: '#fcfcfc',
                        border: `1.5px solid ${theme.palette.primary.grey10}`,
                      }}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        gap={1}
                        mb={isMobile ? 1 : 1.5}
                      >
                        <Box
                          sx={{
                            width: isMobile ? '24px' : '28px',
                            height: isMobile ? '24px' : '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <img
                            src="/static/icons/magic-wand.svg"
                            style={{
                              width: isMobile ? '26px' : '32px',
                              height: isMobile ? '26px' : '32px',
                            }}
                            alt="magic wand"
                          />
                        </Box>
                        <H5
                          sx={{
                            color: theme.palette.primary.mainBlue,
                            fontSize: isMobile ? '16px' : '18px',
                          }}
                        >
                          {item.category || 'AI 한줄 요약'}
                        </H5>
                      </Box>

                      {item.description &&
                        item.description.split('\n').map((line, index) => (
                          <Box
                            key={index}
                            display="flex"
                            alignItems="flex-start"
                            mb={0.5}
                          >
                            <Box
                              sx={{
                                width: '4px',
                                height: '4px',
                                backgroundColor: theme.palette.text.secondary,
                                borderRadius: '50%',
                                marginTop: '6px',
                                marginRight: '8px',
                                flexShrink: 0,
                              }}
                            />
                            <Small
                              sx={{
                                color: theme.palette.text.secondary,
                                lineHeight: 1.4,
                                fontSize: isMobile ? '14px' : '15px',
                              }}
                            >
                              {line}
                            </Small>
                          </Box>
                        ))}
                    </Box>
                  </Box>

                  {/* Footer */}
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{
                      flexWrap: isMobile ? 'wrap' : 'nowrap',
                      gap: isMobile ? 2 : 3,
                      marginTop: 'auto',
                    }}
                  >
                    <FlexBox
                      sx={{
                        minWidth: isMobile ? 'auto' : '120px',
                        alignItems: 'center',
                      }}
                    >
                      <Small
                        sx={{
                          color: theme.palette.text.secondary,
                          marginRight: '12px',
                        }}
                      >
                        지원 금액
                      </Small>
                      <Small
                        sx={{
                          fontWeight: 'bold',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {item.amount}
                      </Small>
                    </FlexBox>
                    <Box
                      textAlign="right"
                      sx={{ minWidth: isMobile ? 'auto' : '100px' }}
                    >
                      <Small
                        sx={{
                          color: theme.palette.text.secondary,
                          marginRight: '12px',
                        }}
                      >
                        신청 기간
                      </Small>
                      <Small
                        sx={{
                          fontWeight: 'bold',
                          color: theme.palette.text.primary,
                        }}
                      >
                        {item.deadline}
                      </Small>
                    </Box>
                    <Box display="flex" gap={isMobile ? 1 : 1.5}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            transform: isMobile ? 'none' : 'scale(1.05)',
                          },
                        }}
                        onClick={e => {
                          e.stopPropagation();
                          toggleBookmark(item.id);
                        }}
                      >
                        <Bookmark
                          size={isMobile ? 19 : 21}
                          color={
                            bookmarkedItems.has(item.id)
                              ? theme.palette.primary.mainBlue
                              : theme.palette.primary.greytxt
                          }
                          fill={
                            bookmarkedItems.has(item.id)
                              ? theme.palette.primary.mainBlue
                              : 'none'
                          }
                        />
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            backgroundColor: '#f5f5f5',
                            transform: isMobile ? 'none' : 'scale(1.05)',
                          },
                        }}
                        onClick={e => e.stopPropagation()}
                      >
                        <Share2
                          size={isMobile ? 19 : 21}
                          color={theme.palette.primary.greytxt}
                        />
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </StyledCard>
  );
};

export default FundingSectionOnlyScroll;
